/**
 * AppSplash.tsx — Branded splash screen with INLINE SVG logo
 *
 * BRIEF §A1: No <img> tags. SVG markup is embedded directly so there's:
 *   - No network fetch
 *   - No image decode
 *   - No service worker cache risk
 *   - No iOS PWA scaling artefacts
 *
 * Fixed size: 96×96. System font only. Background matches app theme.
 *
 * Place at: src/components/ui/AppSplash.tsx
 */

const splashStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: 99999,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'linear-gradient(165deg, #f8fafb 0%, #eef2f3 100%)',
  fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', system-ui, sans-serif",
};

const textStyle: React.CSSProperties = {
  color: '#6b7280',
  fontSize: 14,
  fontWeight: 500,
  letterSpacing: '0.02em',
  marginBottom: 24,
  textAlign: 'center',
};

const spinnerCSS = `
@keyframes app-splash-spin {
  to { transform: rotate(360deg); }
}
.app-splash-spinner {
  width: 24px;
  height: 24px;
  border: 2.5px solid #e5e7eb;
  border-top-color: #007c86;
  border-radius: 50%;
  animation: app-splash-spin 0.7s linear infinite;
}
`;

/** Inline SVG of the CoreTrak CT icon — zero network, zero decode, zero corruption */
function CoreTrakLogo() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="190 25 520 515"
      width={96}
      height={96}
      style={{ marginBottom: 20 }}
      aria-label="CoreTrak"
    >
      <path
        fill="#151515"
        d="M474.43,479.94V231.37H587.11V181.79H275.57a200.54,200.54,0,0,1,327.12-31l43.62-26.5A250.51,250.51,0,0,0,450,29.75c-103.5,0-192.34,62.62-230.73,152a243.68,243.68,0,0,0-15.45,49.58,252.91,252.91,0,0,0-4.88,49.48c0,130.21,99.1,237.25,225.94,249.86A248.12,248.12,0,0,0,450,532c8.25,0,16.42-.4,24.45-1.2a250.38,250.38,0,0,0,170.64-91.82L601.4,412.39A200.11,200.11,0,0,1,474.43,479.94Zm-49.57-.09c-98.92-12.34-175.43-96.75-175.43-199a202.19,202.19,0,0,1,6.12-49.48H424.86Z"
      />
      <path
        fill="#007c86"
        d="M701.08,280.85A250,250,0,0,1,662.6,414.52l-43.22-26.23a200.73,200.73,0,0,0,1.06-213.24l43.18-26.19A250,250,0,0,1,701.08,280.85Z"
      />
    </svg>
  );
}

const subtitleStyle: React.CSSProperties = {
  color: '#9ca3af',
  fontSize: 12,
  fontWeight: 400,
  letterSpacing: '0.01em',
  marginTop: -18,
  marginBottom: 24,
  textAlign: 'center',
};

interface AppSplashProps {
  message?: string;
  subtitle?: string;
}

export function AppSplash({ message = 'Loading CoreTrak…', subtitle }: AppSplashProps) {
  return (
    <div style={splashStyle}>
      <style>{spinnerCSS}</style>
      <CoreTrakLogo />
      <div style={textStyle}>{message}</div>
      {subtitle && <div style={subtitleStyle}>{subtitle}</div>}
      <div className="app-splash-spinner" />
    </div>
  );
}
